import { zodResolver } from '@hookform/resolvers/zod';
import { isAfter, isValid } from 'date-fns';
import { AlertTriangle, ArrowLeft, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useDeleteVehicles, useFleet } from '@/hooks/api/use-fleet';
import useBrandName from '@/hooks/use-brand-name';
import { DATE_ISO, formatDate, toDate } from '@/lib/dates';
import { sortieVehiculesSchema, type SortieVehiculesValues } from '@/schemas/sortie-vehicules';
import type { Contract } from '@/types/api/contract';
import type { FleetVehicle } from '@/types/api/fleet';

/**
 * Date de sortie minimale (`yyyy-MM-dd`) : l'entrée du véhicule le plus récent. Un véhicule sans
 * date d'entrée ne restreint rien ; aucune date connue → `''`, pas de minimum.
 */
export const getMinDateSortie = (vehicules: FleetVehicle[]): string => {
    const mostRecentDate = vehicules.reduce<Date | undefined>((latestDate, vehicule) => {
        const vehicleDate = toDate(vehicule.dateEntree);
        return isValid(vehicleDate) && (!latestDate || isAfter(vehicleDate, latestDate)) ? vehicleDate : latestDate;
    }, undefined);
    return mostRecentDate ? formatDate(mostRecentDate, DATE_ISO) : '';
};

export interface SortieVehiculesProps {
    contract: Contract;
}

const SortieVehicules = ({ contract }: SortieVehiculesProps) => {
    const brandName = useBrandName();
    const contract_id = contract.id;
    const flotteQuery = useFleet(contract_id);
    const flotteData = useMemo(() => flotteQuery.data?.data ?? [], [flotteQuery.data]);
    const { mutate: postDeleteVehicles, isPending: loadingDelete } = useDeleteVehicles();
    const navigate = useNavigate();

    const [search, setSearch] = useState('');

    const { handleSubmit, register, setValue, watch, formState: { errors } } = useForm<SortieVehiculesValues>({
        defaultValues: { selected_vehicules: [], date_sortie: new Date(), contract_id },
        resolver: zodResolver(sortieVehiculesSchema) as unknown as Resolver<SortieVehiculesValues>,
    });
    const values = watch();
    const selected = values.selected_vehicules || [];

    const minDate = useMemo(() => getMinDateSortie(flotteData), [flotteData]);

    const vehiculesActifs = useMemo(
        () => flotteData.filter(v => !v.inactif),
        [flotteData]
    );

    const filteredVehicules = useMemo(() => {
        if (!search) return vehiculesActifs;
        const q = search.toLowerCase();
        return vehiculesActifs.filter(v =>
            (v.immatriculation || '').toLowerCase().includes(q) ||
            (v.marque || '').toLowerCase().includes(q) ||
            (v.modele || '').toLowerCase().includes(q)
        );
    }, [vehiculesActifs, search]);

    const allSelected = vehiculesActifs.length > 0 && vehiculesActifs.every(v => selected.includes(v.adhesion as string | number));
    const isLastVehicle = allSelected;

    const toggleVehicule = (adhesion: string | number) => {
        const next = selected.includes(adhesion)
            ? selected.filter(a => a !== adhesion)
            : [...selected, adhesion];
        setValue('selected_vehicules', next, { shouldValidate: true });
    };

    const toggleAll = () => {
        const next = allSelected ? [] : vehiculesActifs.map(v => v.adhesion as string | number);
        setValue('selected_vehicules', next, { shouldValidate: true });
    };

    const onSubmit = (values: SortieVehiculesValues) => postDeleteVehicles({
        form: values,
        successFunction: () => navigate(`/mes-contrats/mon-contrat/${contract_id}`),
    });

    if (flotteQuery.isPending) return <SmallSpinner />;

    return (
        <div className="space-y-6 mt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                    <Trash2 className="w-6 h-6 text-destructive" />
                    Sortie des véhicules de la flotte
                </h1>
                <Button asChild variant="outline" size="sm">
                    <Link to={`/mes-contrats/mon-contrat/${contract_id}`}>
                        <ArrowLeft className="w-4 h-4" />
                        Retour au contrat
                    </Link>
                </Button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <input type="hidden" {...register('contract_id')} value={contract_id} />

                {/* Date de sortie */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg">Date de sortie</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="space-y-2 max-w-sm">
                            <label htmlFor="date_sortie" className="text-sm font-medium">
                                Date effective de sortie
                            </label>
                            <Input
                                id="date_sortie"
                                type="date"
                                min={minDate}
                                {...register('date_sortie')}
                                aria-invalid={!!errors.date_sortie}
                            />
                            {errors.date_sortie && (
                                <p className="text-sm text-destructive">{errors.date_sortie.message}</p>
                            )}
                            {minDate && (
                                <p className="text-xs text-muted-foreground">
                                    La date doit être postérieure à l'entrée du véhicule le plus récent ({formatDate(minDate)}).
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Véhicules à supprimer */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <CardTitle className="text-lg">Véhicules à supprimer</CardTitle>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {selected.length} sélectionné{selected.length > 1 ? 's' : ''} sur {vehiculesActifs.length} actif{vehiculesActifs.length > 1 ? 's' : ''}
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={toggleAll}
                                disabled={vehiculesActifs.length === 0}
                            >
                                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="relative max-w-sm">
                            <label htmlFor="sortie-search" className="sr-only">Rechercher</label>
                            <Input
                                id="sortie-search"
                                type="text"
                                placeholder="Rechercher (immat, marque, modèle)"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pr-9"
                            />
                            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                        </div>

                        {filteredVehicules.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">
                                {vehiculesActifs.length === 0
                                    ? 'Aucun véhicule actif sur ce contrat.'
                                    : 'Aucun véhicule ne correspond à votre recherche.'}
                            </p>
                        ) : (
                            <div className="rounded-md border divide-y">
                                {filteredVehicules.map((v) => {
                                    const checked = selected.includes(v.adhesion as string | number);
                                    return (
                                        <label
                                            key={v.adhesion}
                                            htmlFor={`vehicule_${v.adhesion}`}
                                            className="flex items-center gap-4 p-6 cursor-pointer hover:bg-muted/50 transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                id={`vehicule_${v.adhesion}`}
                                                checked={checked}
                                                onChange={() => toggleVehicule(v.adhesion as string | number)}
                                                className="h-4 w-4 rounded-md border-input text-primary focus:ring-2 focus:ring-ring/50 cursor-pointer"
                                            />
                                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
                                                <div>
                                                    <p className="text-xs text-muted-foreground">Immatriculation</p>
                                                    <p className="font-mono text-sm uppercase font-medium">
                                                        {v.immatriculation || '—'}
                                                    </p>
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <p className="text-xs text-muted-foreground">Véhicule</p>
                                                    <p className="text-sm font-medium">
                                                        {`${v.marque || ''} ${v.modele || ''}`.trim() || '—'}
                                                    </p>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        {errors.selected_vehicules && (
                            <p className="text-sm text-destructive">{errors.selected_vehicules.message}</p>
                        )}
                    </CardContent>
                </Card>

                {/* Warning dernier véhicule */}
                {isLastVehicle && (
                    <Alert className="bg-warning/10 text-warning-foreground border-warning/30">
                        <AlertTriangle className="!h-5 !w-5 text-warning" />
                        <AlertDescription>
                            <span className="font-semibold">Conseil {brandName} :</span> en retirant le dernier véhicule de votre contrat, celui-ci sera automatiquement terminé. Si vous souhaitez remplacer votre véhicule, ajoutez d'abord le nouveau avant de procéder à la sortie.
                        </AlertDescription>
                    </Alert>
                )}

                {/* Disclaimer légal */}
                <Alert className="bg-warning/10 border-warning/30">
                    <AlertTriangle className="!h-5 !w-5 text-warning" />
                    <AlertDescription>
                        <p className="font-medium mb-1">Par véhicule, vous déclarez qu'au moins une des conditions suivantes est respectée :</p>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Le véhicule est vendu et le souscripteur dispose d'un certificat de vente</li>
                            <li>Le véhicule est volé et le souscripteur dispose d'une plainte pour le vol du véhicule</li>
                        </ul>
                    </AlertDescription>
                </Alert>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <Button asChild variant="outline" type="button">
                        <Link to={`/mes-contrats/mon-contrat/${contract_id}`}>Annuler</Link>
                    </Button>
                    <Button
                        type="submit"
                        variant="destructive"
                        loading={loadingDelete}
                        loadingText="Validation…"
                        disabled={selected.length === 0}
                    >
                        <Trash2 className="w-4 h-4" />
                        Valider la sortie
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default SortieVehicules;
